/**
 * Collaborator helpers — shared across domains that follow the
 * "AccessControl vs Collaborator" UX guide:
 *
 *   - DynamicEntity   GET  /dynamicEntity/config/collaborators/{id}
 *   - App             GET  /app/config/collaborators/{id}
 *   - AIAgent         GET  /ai/agent/config/collaborators/{id}
 *   - CustomList      GET  /customList/config/collaborators/{id}
 *   - AIAgentSchedule embedded `collaborators: BaseConfigSimpleUser[]`
 *     (PATCH/PUT on `/ai/schedule`, not a separate config route)
 *
 * The backend response shape varies slightly between domains (some include
 * the user via `$expand`, some only return the FK), so we normalize on the
 * frontend with `resolveCollaboratorUser` and, when names are missing, we
 * resolve them with a single batched `user/paged` request.
 */

import { getAll } from '@/services/api';

/**
 * Extracts the user-shaped fields from a raw collaborator record.
 * Handles every shape we have seen in the backend:
 *   { collaboratorUser: { id, fullName, ... } }   (DynamicEntity)
 *   { collaborator: { id, fullName, ... } }       (CustomList/App/AIAgent)
 *   { _user: { id, fullName, ... } }              (hydrated on the FE)
 *   { id, fullName, ... }                         (flat)
 *   { collaboratorId } / { collaboratorUserId }   (only the FK is exposed)
 */
export const resolveCollaboratorUser = (item) => {
  if (!item) return null;
  const nested = item._user || item.collaboratorUser || item.collaborator;
  if (nested) {
    return {
      id: nested.id || nested.userId || item.collaboratorId || item.collaboratorUserId,
      fullName: nested.fullName,
      names: nested.names,
      lastNames: nested.lastNames,
    };
  }
  if (item.fullName || item.names || item.name) {
    return {
      id: item.id || item.collaboratorId || item.collaboratorUserId,
      fullName: item.fullName || item.name,
      names: item.names,
      lastNames: item.lastNames,
    };
  }
  return {
    id: item.collaboratorId || item.collaboratorUserId || item.id,
  };
};

/** True when the row already has user-display data available. */
const hasUserInfo = (item) =>
  !!(
    item?._user?.fullName ||
    item?.collaboratorUser?.fullName ||
    item?.collaborator?.fullName ||
    item?.fullName ||
    item?.names ||
    item?.name
  );

const extractUserId = (item) => item?.collaboratorId || item?.collaboratorUserId || null;

/**
 * Augments a list of raw collaborator records with `_user: { id, fullName, ... }`
 * for every record that arrived from the backend without user-display fields.
 *
 * Resolves user info with a single batched `user/paged` request, so the
 * cost is at most one extra round-trip regardless of the response shape
 * returned by the collaborator endpoint.
 *
 * Safe to call unconditionally — if every record already carries user info,
 * the function returns the input untouched without hitting the API.
 */
export async function hydrateCollaboratorUsers(records) {
  if (!Array.isArray(records) || records.length === 0) return [];

  const pending = records.filter((r) => !hasUserInfo(r) && extractUserId(r));
  if (!pending.length) return records;

  const ids = Array.from(new Set(pending.map(extractUserId).filter(Boolean)));
  if (!ids.length) return records;

  try {
    const filter = ids.map((id) => `id eq ${id}`).join(' or ');
    const res = await getAll({
      route: 'user/paged',
      select: 'id,fullName,names,lastNames',
      filter,
      limit: ids.length,
    });
    const items = res?.items || res?.data?.items || [];
    if (!Array.isArray(items) || !items.length) return records;
    const byId = items.reduce((acc, u) => {
      if (u && u.id) acc[u.id] = u;
      return acc;
    }, {});

    return records.map((r) => {
      if (hasUserInfo(r)) return r;
      const uid = extractUserId(r);
      const user = uid ? byId[uid] : null;
      return user ? { ...r, _user: user } : r;
    });
  } catch {
    return records;
  }
}
